import { useEffect, useState } from "react";
import type { TrackedOrder } from "../../api/types";
import { t } from "../../i18n";
import { TrackCard } from "./TrackCard";

interface Props {
  token: string;
}

/** How often the page asks again while the order is under way. */
const REFRESH_MS = 30_000;
/** While the courier is on the way: his phone tells where he is every few seconds. */
const RIDE_REFRESH_MS = 10_000;

/** The courier left with it and is not at the door yet. */
export function onTheRoad(order: Pick<TrackedOrder, "channel" | "status" | "departedAt" | "deliveredAt">): boolean {
  return (
    order.channel === "delivery" &&
    order.status !== "cancelled" &&
    order.departedAt !== null &&
    order.deliveredAt === null
  );
}

/** Still under way: a delivery until it is delivered, a takeaway until it is paid; never when cancelled. */
export function underWay(order: Pick<TrackedOrder, "channel" | "status" | "deliveredAt">): boolean {
  if (order.status === "cancelled") return false;
  return order.channel === "delivery" ? order.deliveredAt === null : order.status === "open";
}

/**
 * "Volg je bestelling" (prototype openTrack): the customer's page behind the link in the mail, on any
 * phone, without signing in. It shows the shop, when the order is ready or arrives, the courier on the
 * map once he left and what is still to be paid, and asks again every 30 seconds while it is under way.
 */
export function TrackPage({ token }: Props) {
  const text = t.track;
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = () => {
      fetch(`/api/v1/track/${encodeURIComponent(token)}`)
        .then(async (response) => {
          if (stopped) return;
          if (!response.ok) {
            setGone(true);
            return;
          }
          const answer = (await response.json()) as { order: TrackedOrder };
          setOrder(answer.order);
          if (underWay(answer.order)) {
            timer = setTimeout(load, onTheRoad(answer.order) ? RIDE_REFRESH_MS : REFRESH_MS);
          }
        })
        .catch(() => {
          if (!stopped) timer = setTimeout(load, REFRESH_MS);
        });
    };
    load();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [token]);

  useEffect(() => {
    document.title = order ? `${text.title} · ${order.shop.name}` : text.title;
  }, [order, text.title]);

  if (gone) {
    return (
      <main className="trackpage">
        <p className="trackgone">{text.gone}</p>
      </main>
    );
  }
  if (!order) return <main className="trackpage" aria-busy="true" />;

  return (
    <main className="trackpage">
      <h1>{text.title}</h1>
      <TrackCard order={order} />
    </main>
  );
}

/** The token when this page is the customer's page of an order (/t/<token>), else null. */
export function trackToken(pathname: string): string | null {
  return /^\/t\/([A-Za-z0-9]{16})\/?$/.exec(pathname)?.[1] ?? null;
}
